/**
 * Mehrbedarfe nach § 21 SGB II.
 *
 * Implementiert sind die im Konzeptdokument referenzierten Mehrbedarfs-
 * tatbestände. Prozentuale Mehrbedarfe werden auf den maßgeblichen
 * Regelbedarf des betreffenden Mitglieds (i.d.R. RBS 1 oder RBS 2)
 * angewendet.
 *
 * Die hier hinterlegten Prozentsätze sind die im SGB II festgelegten
 * Standardsätze. Härtefall-Regelungen (§ 21 Abs. 6 SGB II) sind nicht
 * automatisierbar und werden manuell vom Berater erfasst — sie sind
 * nicht Teil dieser Funktion.
 */
import type { MoneyCents } from '../schema/brands.js';
import type { BgComposition, BgMember } from '../schema/bgComposition.js';
import { REGELBEDARFE_2026_CENTS } from './regelbedarfe2026.js';

const SCHWANGERSCHAFT_PERCENT = 0.17; // § 21 Abs. 2 SGB II
const KOSTENAUFWAENDIGE_ERNAEHRUNG_PERCENT = 0.1; // § 21 Abs. 5 SGB II (pauschal 10%)
const DEZENTRALE_WARMWASSER_TIER_RBS1_PERCENT = 0.023; // § 21 Abs. 7 SGB II
const DEZENTRALE_WARMWASSER_TIER_KIND_PERCENT = 0.014;

/**
 * Zählt Kinder in der BG nach Altersgruppen — wird für den Alleinerziehenden-
 * Mehrbedarf benötigt. Die Altersgrenzen sind „strikt unter 7" bzw.
 * „strikt unter 16", in Übereinstimmung mit § 21 Abs. 3 SGB II.
 */
export function countChildrenInBg(bg: BgComposition): { under7: number; under16: number } {
  let under7 = 0;
  let under16 = 0;
  for (const m of bg.members) {
    if (m.role !== 'kind') continue;
    if (m.age < 7) under7 += 1;
    if (m.age < 16) under16 += 1;
  }
  return { under7, under16 };
}

/**
 * Mehrbedarf für Alleinerziehende, § 21 Abs. 3 SGB II:
 *   - 36% bei einem Kind unter 7 Jahren ODER 2-3 Kindern unter 16
 *   - sonst 12% pro Kind, maximal 60%
 *
 * Die Anzahl der Kinder wird aus der BG-Komposition abgeleitet (Mitglieder
 * mit Rolle „kind", gefiltert nach Alter). Die früheren Member-Felder
 * `numberOfChildrenUnder7/16` waren redundant, im UI nie befüllt und führten
 * dazu, dass der Mehrbedarf in der Praxis immer 0% war.
 */
function computeAlleinerziehendMehrbedarf(bg: BgComposition): number {
  const { under7, under16 } = countChildrenInBg(bg);
  const numberOfChildren = under16; // Kinder ab 16 zählen für diesen Mehrbedarf nicht

  if (numberOfChildren === 0) return 0;

  // Tatbestand „ein Kind <7 ODER 2-3 Kinder <16"
  if (under7 >= 1 || (under16 >= 2 && under16 <= 3)) {
    return Math.max(0.36, Math.min(0.6, 0.12 * numberOfChildren));
  }
  // Sonst 12% pro Kind, max 60%
  return Math.min(0.6, 0.12 * numberOfChildren);
}

function getMemberBaseRegelbedarfCents(member: BgMember): MoneyCents {
  if (member.role === 'antragsteller') return REGELBEDARFE_2026_CENTS.RBS1;
  if (member.role === 'partner') return REGELBEDARFE_2026_CENTS.RBS2;
  // kind
  if (member.age <= 5) return REGELBEDARFE_2026_CENTS.RBS6;
  if (member.age <= 13) return REGELBEDARFE_2026_CENTS.RBS5;
  return REGELBEDARFE_2026_CENTS.RBS4;
}

/**
 * Mehrbedarf eines einzelnen Mitglieds. Für den Alleinerziehenden-Mehrbedarf
 * wird die ganze BG benötigt (Kinderzählung). Wenn `bg` nicht übergeben wird,
 * fällt der Single-Parent-Tatbestand auf 0% — das ist die sichere Default-
 * Annahme für Aufrufer, die nur ein einzelnes Mitglied prüfen wollen.
 */
export function computeMehrbedarfPerMember(member: BgMember, bg?: BgComposition): MoneyCents {
  const base = getMemberBaseRegelbedarfCents(member) as number;
  let mehrbedarfPercent = 0;

  for (const need of member.specialNeeds) {
    switch (need) {
      case 'pregnancy':
        mehrbedarfPercent += SCHWANGERSCHAFT_PERCENT;
        break;
      case 'single_parent':
        mehrbedarfPercent += bg ? computeAlleinerziehendMehrbedarf(bg) : 0;
        break;
      case 'costly_nutrition':
        mehrbedarfPercent += KOSTENAUFWAENDIGE_ERNAEHRUNG_PERCENT;
        break;
      case 'decentralized_hot_water':
        mehrbedarfPercent +=
          member.role === 'kind'
            ? DEZENTRALE_WARMWASSER_TIER_KIND_PERCENT
            : DEZENTRALE_WARMWASSER_TIER_RBS1_PERCENT;
        break;
      case 'disability_workintegration':
        // § 21 Abs. 4: 35% bei Eingliederungshilfe für Erwerbsfähige
        mehrbedarfPercent += 0.35;
        break;
    }
  }

  return Math.round(base * mehrbedarfPercent) as MoneyCents;
}

export function computeTotalMehrbedarfCents(bg: BgComposition): MoneyCents {
  return bg.members.reduce(
    (sum, m) => sum + (computeMehrbedarfPerMember(m, bg) as number),
    0,
  ) as MoneyCents;
}
